import React, { useMemo } from "react";
import { Dropdown, DropdownToggleType } from "../Components/Dropdown/Dropdown";
import "./TimePicker.scss";

export interface TimePickerProps {
  value: string;
  onChange: (time: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const convertTo12Hour = (hour24: number): { hour12: number; period: "AM" | "PM" } => {
  if (hour24 === 0) {
    return { hour12: 12, period: "AM" };
  }
  if (hour24 === 12) {
    return { hour12: 12, period: "PM" };
  }
  if (hour24 > 12) {
    return { hour12: hour24 - 12, period: "PM" };
  }
  return { hour12: hour24, period: "AM" };
};

const convertDisplayTo24Hour = (displayTime: string): string => {
  if (!displayTime) {
    return "";
  }

  const match = displayTime.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) {
    return "";
  }

  const hour12 = parseInt(match[1], 10);
  const minute = match[2];
  const period = match[3].toUpperCase() as "AM" | "PM";

  if (hour12 < 1 || hour12 > 12) {
    return "";
  }

  let hour24: number;
  if (period === "AM") {
    hour24 = hour12 === 12 ? 0 : hour12;
  } else {
    hour24 = hour12 === 12 ? 12 : hour12 + 12;
  }

  return `${hour24.toString().padStart(2, "0")}:${minute}`;
};

const convert24HourToDisplay = (time24: string): string => {
  if (!time24 || !time24.includes(":")) {
    return "";
  }

  const [h, m] = time24.split(":");
  const hourNum = parseInt(h, 10);
  const minuteNum = parseInt(m, 10);

  if (isNaN(hourNum) || hourNum < 0 || hourNum > 23) {
    return "";
  }
  if (isNaN(minuteNum) || minuteNum < 0 || minuteNum > 59) {
    return "";
  }
  const { hour12, period } = convertTo12Hour(hourNum);
  const minute = m.padStart(2, "0");

  return `${hour12}:${minute} ${period}`;
};

const TimePicker: React.FC<TimePickerProps> = ({
  value,
  onChange,
  placeholder = "Select time",
  disabled = false,
}) => {
  const timeOptions = useMemo(() => {
    const options: string[] = [];

    for (let hour = 12; hour <= 12; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const minuteStr = minute.toString().padStart(2, "0");
        options.push(`${hour}:${minuteStr} AM`);
      }
    }
    for (let hour = 1; hour <= 11; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const minuteStr = minute.toString().padStart(2, "0");
        options.push(`${hour}:${minuteStr} AM`);
      }
    }

    for (let hour = 12; hour <= 12; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const minuteStr = minute.toString().padStart(2, "0");
        options.push(`${hour}:${minuteStr} PM`);
      }
    }
    for (let hour = 1; hour <= 11; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const minuteStr = minute.toString().padStart(2, "0");
        options.push(`${hour}:${minuteStr} PM`);
      }
    }

    return options;
  }, []);

  const displayValue = useMemo(() => {
    return convert24HourToDisplay(value);
  }, [value]);

  const handleTimeChange = (selectedTime: string) => {
    const time24 = convertDisplayTo24Hour(selectedTime);
    if (time24) {
      onChange(time24);
    }
  };

  return (
    <div className="timePicker">
      <div className="timePickerContainer">
        <div className="timePickerDropdown">
          <Dropdown
            type={DropdownToggleType.OUTLINED}
            background="light"
            size="lg"
            options={timeOptions}
            onChange={handleTimeChange}
            placeholder={placeholder}
            value={displayValue}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
};

export default TimePicker;
